import { createMediaCollection, type CreateMediaCollection } from '@/api/core/property-media-write';
import { isApiError } from '@/api/http/errors';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useState } from 'react';
import type { PropertyRuntime } from '../property-queries';
import {
  canManageMedia,
  confirmMediaWrite,
  useMediaScope,
  useMediaWriteOwner,
  verifyMediaActor,
} from './media-write-form';

export function OwnMediaCreate({
  runtime,
  generation,
  owner,
  onSaved,
  existingCollectionId,
}: {
  runtime: PropertyRuntime;
  generation: number;
  owner: Omit<CreateMediaCollection, 'command_id'>;
  onSaved: (id: string) => Promise<void>;
  existingCollectionId?: string | null;
}) {
  const state = useMediaWriteOwner();
  const { createAttempt, attempt } = state;
  const { mounted, track } = useMediaScope(state);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const matches =
    createAttempt?.body.owner_scope_kind === owner.owner_scope_kind &&
    createAttempt.body.owner_property_id === owner.owner_property_id &&
    createAttempt.body.owner_unit_id === owner.owner_unit_id &&
    createAttempt.body.owner_room_id === owner.owner_room_id &&
    createAttempt.body.owner_room_type_id === owner.owner_room_type_id;
  const submit = async (body: CreateMediaCollection, originalActor?: string) => {
    if (busy || !canManageMedia(runtime, generation)) return;
    const command = createAttempt?.body === body ? createAttempt : { body };
    const lease = state.claim(command);
    if (!lease) return;
    track(lease);
    let received = false;
    const current = () => mounted() && state.current(lease) && canManageMedia(runtime, generation);
    setBusy(true);
    setMessage('');
    try {
      const actorId = await verifyMediaActor(runtime, generation);
      if (!current()) return;
      if (!actorId || !state.bind(lease, actorId)) {
        setMessage('현재 수행자를 확인할 수 없어 사진 추가를 준비할 수 없습니다.');
        return;
      }
      lease.sent = true;
      const receipt = await createMediaCollection(runtime.client, body);
      received = true;
      if (!current()) return;
      state.checking(lease);
      await confirmMediaWrite(
        runtime,
        body.owner_property_id,
        {
          kind: body.owner_scope_kind === 'UNIT' ? 'SLOT' : body.owner_scope_kind,
          id: body.owner_scope_kind === 'UNIT' ? body.owner_unit_id : body.owner_room_type_id,
          floor: null,
        },
        receipt,
        current,
      );
      if (!current()) return;
      state.clear(lease);
      if (receipt.collectionId) await onSaved(receipt.collectionId);
    } catch (error) {
      if (!current()) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('property', generation);
      if (
        !received &&
        isApiError(error) &&
        (error.status === 403 || error.status === 422 || (error.status === 409 && !originalActor))
      )
        state.clear(lease);
      if (canManageMedia(runtime, generation))
        setMessage(
          isApiError(error) && error.status === 409
            ? '사진 그룹 목록이 바뀌었습니다. 다시 조회해 주세요.'
            : isApiError(error) && error.status === 422
              ? '그룹 정보를 확인해 주세요.'
              : '결과를 확인하지 못했습니다. 같은 요청을 다시 시도해 주세요.',
        );
    } finally {
      state.release(lease);
      if (mounted()) setBusy(false);
    }
  };
  if (!canManageMedia(runtime, generation)) return null;
  return (
    <div className="my-3 flex flex-wrap items-center gap-3" aria-busy={busy || !!state.progress}>
      {state.progress && <p role="status">{state.progress}</p>}
      <Button
        disabled={busy || !!createAttempt || !!attempt || !!state.editAttempt}
        onClick={() =>
          existingCollectionId
            ? void onSaved(existingCollectionId)
            : void submit({ ...owner, command_id: crypto.randomUUID() } as CreateMediaCollection)
        }
      >
        사진 추가
      </Button>
      {matches && !busy && !state.progress && (
        <Button
          variant="outline"
          onClick={() => void submit(createAttempt.body, createAttempt.actorId)}
        >
          같은 요청 재시도
        </Button>
      )}
      {createAttempt && !matches && <p>이전 사진 그룹의 등록 결과를 먼저 확인해 주세요.</p>}
      {message && <p role="status">{message}</p>}
    </div>
  );
}
